import FormValidations from 'components/FormValidations/FormValidations';
import * as mapYourMedsConstant from 'components/common/mapYourMedsConstant';

/**
 * Field rules shared by every screen that creates a user account: the admin
 * "User Registration" screen (AddFacilityStaff), the registration approval
 * modal and the public self-registration form (userregistrations.md D9/U2.5).
 *
 * The predicates wrap the same FormValidations types AddFacilityStaff uses, and
 * validateStaffPersonalFields() returns AddFacilityStaff's exact messages, so a
 * user approved from the queue is held to the same rules as one created by
 * hand. Client validation is a courtesy — the server re-checks everything.
 */

export const PHONE_MASK = [
  '(',
  /[1-9]/,
  /\d/,
  /\d/,
  ')',
  ' ',
  /\d/,
  /\d/,
  /\d/,
  '-',
  /\d/,
  /\d/,
  /\d/,
  /\d/
];

export const STATE_OPTIONS = [
  { key: 'Select a state', value: '' },
  ...mapYourMedsConstant.STATE_VALUES
];

const STATE_CODES = new Set(
  mapYourMedsConstant.STATE_VALUES.map(state => state.value)
);

export const digitsOnly = value => String(value ?? '').replace(/\D/g, '');

const text = value => String(value ?? '').trim();

// Letters (the server's "alphabetic" rule, same regex AddFacilityStaff uses).
export const isValidName = value => FormValidations(text(value), 1);
export const isValidCity = value => FormValidations(text(value), 1);
export const isValidEmail = value => FormValidations(text(value), 5);
export const isValidUsername = value => FormValidations(text(value), 2);
// Numeric: exactly 10 digits once the (XXX) XXX-XXXX mask is stripped.
export const isValidMobile = value =>
  FormValidations(String(value ?? ''), 6, 10);
// Numeric: 5-digit ZIP.
export const isValidZip = value => /^\d{5}$/.test(text(value));
// Alphabetic: one of the two-letter codes offered by the State dropdown.
export const isValidState = value =>
  /^[A-Za-z]{2}$/.test(text(value)) &&
  STATE_CODES.has(text(value).toUpperCase());

/**
 * Personal-information rules with AddFacilityStaff's messages. Expects
 * AddFacilityStaff's field names: fname, lname, mobile, email, city, state,
 * zip.
 */
export const validateStaffPersonalFields = (values = {}) => {
  const errors = {};

  if (!text(values.fname)) {
    errors.fname = 'First Name is required.';
  } else if (!isValidName(values.fname)) {
    errors.fname = 'Only letters are allowed';
  }

  if (!text(values.lname)) {
    errors.lname = 'Last Name is required.';
  } else if (!isValidName(values.lname)) {
    errors.lname = 'Only letters are allowed';
  }

  if (text(values.mobile) && !isValidMobile(values.mobile)) {
    errors.mobile = 'Invalid mobile number format.';
  }

  if (text(values.city) && !isValidCity(values.city)) {
    errors.city = 'City should be in letters';
  }

  if (text(values.state) && !isValidState(values.state)) {
    errors.state = 'Select a valid state.';
  }

  if (text(values.zip) && !isValidZip(values.zip)) {
    errors.zip = 'Zip should be 5 digits.';
  }

  if (!text(values.email)) {
    errors.email = 'Email is required.';
  } else if (!isValidEmail(values.email)) {
    errors.email = 'Invalid email format.';
  }

  return errors;
};

export const validateStaffUsername = value => {
  if (!text(value)) {
    return 'Username is required.';
  }
  if (!isValidUsername(value)) {
    return 'Invalid username format.';
  }
  return '';
};

/** Username suggestion from an email's local part, e.g. "jane.doe". */
export const suggestUsernameFromEmail = email =>
  text(email)
    .split('@')[0]
    .replace(/[^a-zA-Z0-9._-]/g, '')
    .slice(0, 30);

/* ------------------------------------------------------------------------ */
/*               Role list — mirrors AddFacilityStaff's rules               */
/* ------------------------------------------------------------------------ */

const readJson = (key, fallback) => {
  try {
    const value = JSON.parse(localStorage.getItem(key));
    return value ?? fallback;
  } catch (error) {
    return fallback;
  }
};

const accessControlValues = () =>
  String(localStorage.getItem('accessControl') || '')
    .split(',')
    .map(Number)
    .filter(value => !Number.isNaN(value));

/**
 * Day-program / SharedLiving flags of the TARGET group (like
 * AddFacilityStaff.getGroupFlag): pharmacy roles (<= 3) read the picked
 * group's `access_control` from the cached groupsData; group admins (role 4)
 * are locked to their own group and read the flags cached at login.
 * Backend mapping: 4 = day programs, 5 = SharedLiving.
 */
export const getGroupFeatureFlags = (groupId, approverRoleId) => {
  if (Number(approverRoleId) <= 3) {
    const group = readJson('groupsData', []).find(
      option => Number(option.value) === Number(groupId)
    );
    if (group && Array.isArray(group.access_control)) {
      const flags = group.access_control.map(Number);
      return {
        isDayProgramActive: flags.includes(4),
        isSharedLivingActive: flags.includes(5),
        known: true
      };
    }
    // Group not cached (e.g. dashboard never loaded): don't guess "off" —
    // offer the options and let the server decide.
    return {
      isDayProgramActive: true,
      isSharedLivingActive: true,
      known: false
    };
  }

  const ownFlags = accessControlValues();
  return {
    isDayProgramActive:
      Number(readJson('is_dayprogram_active', 0)) === 1 || ownFlags.includes(4),
    isSharedLivingActive: ownFlags.includes(5),
    known: true
  };
};

/**
 * Roles an approver may grant for a group-bound account, capped at the
 * approver's own privilege (lower number = more privileged, so a role-4 group
 * admin may grant 4, 5, 6 or 7 — never 1–3) and with role 7 limited to
 * approvers of role <= 4 (SL-026), like AddFacilityStaff.
 *
 * A usability affordance only — the server enforces the same rule (R4.2) and
 * its error must still be shown if this list is ever bypassed.
 */
export const buildGrantableRoleOptions = ({
  approverRoleId,
  isDayProgramActive = true,
  isSharedLivingActive = true
}) => {
  const approver = Number(approverRoleId);
  const roles = [
    { key: 'Group Admin', value: 4 },
    { key: 'Facility Staff', value: 5 },
    { key: 'Day Program User', value: 6 },
    ...(approver <= 4 ? [{ key: 'SharedLiving Caregiver', value: 7 }] : [])
  ];

  return roles
    .filter(
      role =>
        Number.isFinite(approver) && approver > 0 && role.value >= approver
    )
    .map(role => {
      if (role.value === 6 && !isDayProgramActive) {
        return {
          ...role,
          key: `${role.key} (Day Programs not enabled for this group)`,
          disabled: true
        };
      }
      if (role.value === 7 && !isSharedLivingActive) {
        return {
          ...role,
          key: `${role.key} (SharedLiving not enabled for this group)`,
          disabled: true
        };
      }
      return role;
    });
};

/* ------------------------------------------------------------------------ */
/*                      Facility / day-program options                      */
/* ------------------------------------------------------------------------ */

/**
 * Facilities an approver can grant inside a group, from the facilitiesData
 * cache the dashboard builds at login ({ key, value, group_id }).
 */
export const getCachedGroupFacilities = groupId => {
  const facilities = readJson('facilitiesData', []);
  const list = Array.isArray(facilities) ? facilities : [];
  const inGroup = groupId
    ? list.filter(facility => Number(facility.group_id) === Number(groupId))
    : list;
  return inGroup.map(facility => ({
    value: facility.value,
    label: facility.key,
    group_id: facility.group_id
  }));
};

/** Group name from the groupsData cache (pharmacy roles only). */
export const getCachedGroupName = groupId => {
  const group = readJson('groupsData', []).find(
    option => Number(option.value) === Number(groupId)
  );
  return group?.key || '';
};

/**
 * Groups a pharmacy approver (roles 1–3) can assign, from the groupsData cache
 * — the same source as AddFacilityStaff's Group dropdown.
 */
export const getCachedGroupOptions = () => {
  const groups = readJson('groupsData', []);
  return (Array.isArray(groups) ? groups : [])
    .filter(group => group && group.value !== undefined && group.value !== null)
    .map(group => ({
      value: group.value,
      label: String(group.key || `Group #${group.value}`),
      accessControl: Array.isArray(group.access_control)
        ? group.access_control.map(Number)
        : null
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Can this group hold an account with this role? Day Program users need the
 * group's day-program feature (access_control 4), SharedLiving caregivers its
 * SharedLiving feature (5). Unknown flags → allowed; the server has the last
 * word.
 */
export const groupSupportsRole = (group, roleId) => {
  if (!group?.accessControl) {
    return true;
  }
  if (String(roleId) === '6') {
    return group.accessControl.includes(4);
  }
  if (String(roleId) === '7') {
    return group.accessControl.includes(5);
  }
  return true;
};

/** id → name maps of the cached groups / facilities, for table lookups. */
const readNameMap = key => {
  const list = readJson(key, []);
  return new Map(
    (Array.isArray(list) ? list : []).map(item => [
      String(item.value),
      item.key
    ])
  );
};

export const getCachedGroupNameMap = () => readNameMap('groupsData');
export const getCachedFacilityNameMap = () => readNameMap('facilitiesData');

/**
 * Active day programs as select options. Pharmacy roles see every program, so
 * filter by the target group; group admins already get only their own.
 * Mirrors AddFacilityStaff's normalizeDayProgramOptions.
 */
export const toDayProgramOptions = (programs, groupId, approverRoleId) =>
  (Array.isArray(programs) ? programs : [])
    .filter(program => Number(program?.is_active) === 1)
    .filter(program =>
      Number(approverRoleId) <= 3 && groupId
        ? Number(program?.group_id) === Number(groupId)
        : true
    )
    .map(program => ({
      label: program.name || program.day_program_name,
      value: program.id || program.day_program_id
    }))
    .filter(option => option.value !== undefined && option.value !== null)
    .sort((a, b) => String(a.label).localeCompare(String(b.label)));
